import React, { useState, useEffect } from 'react';
import { Collector } from '@/lib/mock-data/collectors-data';
import { CollectorCard } from './collector-card';
import { Search, Filter } from 'lucide-react';

interface CollectorListProps {
  collectors: Collector[];
  title?: string;
  showRank?: boolean;
  showSearch?: boolean;
  showFilter?: boolean;
  showDetails?: boolean;
  removeBorders?: boolean;
  hideIds?: boolean;
  expandable?: boolean;
  cardsWithBorders?: boolean;
}

export function CollectorList({ 
  collectors, 
  title, 
  showRank = false, 
  showSearch = false,
  showFilter = false,
  showDetails = false,
  removeBorders = false,
  hideIds = false,
  expandable = false,
  cardsWithBorders = false
}: CollectorListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTier, setSelectedTier] = useState<string>('All');
  
  // Listen for external search input changes
  useEffect(() => {
    const externalSearchInput = document.getElementById('collector-search') as HTMLInputElement;
    if (externalSearchInput && !showSearch) {
      const handleExternalSearch = (e: Event) => {
        const target = e.target as HTMLInputElement;
        setSearchTerm(target.value);
      };
      
      externalSearchInput.addEventListener('input', handleExternalSearch);
      return () => {
        externalSearchInput.removeEventListener('input', handleExternalSearch);
      };
    }
  }, [showSearch]);
  
  // Listen for external filter changes
  useEffect(() => {
    const externalFilter = document.getElementById('tier-filter') as HTMLSelectElement;
    if (externalFilter && !showFilter) {
      const handleExternalFilter = (e: Event) => {
        const target = e.target as HTMLSelectElement;
        setSelectedTier(target.value);
      };
      
      externalFilter.addEventListener('change', handleExternalFilter);
      return () => {
        externalFilter.removeEventListener('change', handleExternalFilter);
      };
    }
  }, [showFilter]);
  
  // Filter collectors based on search term and selected tier
  const filteredCollectors = collectors.filter(collector => {
    const matchesSearch = collector.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         collector.walletAddress.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = selectedTier === 'All' || collector.tier === selectedTier;
    
    return matchesSearch && matchesTier;
  });
  
  const containerClass = `${removeBorders ? 'bg-transparent' : 'bg-white'} ${removeBorders ? '' : 'border border-zinc-200'} ${removeBorders ? '' : 'rounded-md'} ${removeBorders ? 'p-0' : 'p-4'} ${removeBorders ? '' : 'hover:border-zinc-300'} transition-all duration-300`;
  
  return (
    <div className={containerClass}>
      {title && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-mono uppercase tracking-tight">{title}</h3>
          
          <div className="flex items-center space-x-2">
            {showFilter && (
              <div className="relative">
                <select
                  className="appearance-none text-[10px] bg-zinc-100 border border-zinc-200 rounded-md py-1 pl-2 pr-8 focus:outline-hidden focus:ring-0 focus:border-zinc-800"
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value)}
                >
                  <option value="All">All Tiers</option>
                  <option value="Diamond">Diamond</option>
                  <option value="Platinum">Platinum</option>
                  <option value="Gold">Gold</option>
                  <option value="Silver">Silver</option>
                  <option value="Bronze">Bronze</option>
                </select>
                <Filter className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-zinc-500 pointer-events-none" />
              </div>
            )}
            
            {showSearch && (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search collectors..."
                  className="text-[10px] bg-zinc-100 border border-zinc-200 rounded-md py-1 pl-7 pr-2 w-40 focus:outline-hidden focus:ring-0 focus:border-zinc-800"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-zinc-500" />
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="space-y-3">
        {filteredCollectors.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-xs text-zinc-500">No collectors found</p>
          </div>
        ) : (
          filteredCollectors.map((collector, index) => (
            <CollectorCard 
              key={collector.id} 
              collector={collector} 
              rank={index + 1} 
              showRank={showRank}
              showDetails={showDetails}
              removeBorders={cardsWithBorders ? false : removeBorders}
              hideIds={hideIds}
              expandable={expandable}
            />
          ))
        )}
      </div>
    </div>
  );
}
